import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { uploadGalleryImages } from '../../services/galleryService';
import {
  PHOTO_UPLOAD_CONCURRENCY,
  usePhotoUploadQueue,
  type PhotoUploadAnnouncement,
  type PhotoUploadItem,
  type PhotoUploadStatus,
} from '../photoUpload/usePhotoUploadQueue';

/** At most this many gallery uploads run at the same time. */
export const GALLERY_UPLOAD_CONCURRENCY = PHOTO_UPLOAD_CONCURRENCY;

export type GalleryUploadStatus = PhotoUploadStatus;
export type GalleryUploadItem = PhotoUploadItem;
export type GalleryUploadAnnouncement = PhotoUploadAnnouncement;

/**
 * Upload queue for the gallery (the shared per-file queue, usePhotoUploadQueue):
 * one request per file, at most GALLERY_UPLOAD_CONCURRENCY at a time. The gallery
 * list is refreshed once a batch has finished.
 *
 * Files are sent exactly as the previous single-request upload sent them (field
 * `images`, no extra metadata), so the backend keeps deriving the photo date
 * (photoTakenAt = captured_at ?? taken_at ?? created_at) as before.
 */
export function useGalleryUploadQueue(aquariumId: string) {
  const queryClient = useQueryClient();

  const upload = useCallback(
    async (file: File, onProgress: (percent: number) => void) => {
      await uploadGalleryImages(aquariumId, [file], onProgress);
    },
    [aquariumId],
  );

  const onBatchFinished = useCallback(
    ({ done }: { done: number }) => {
      if (done > 0) {
        void queryClient.invalidateQueries({ queryKey: ['galleryImages', aquariumId] });
      }
    },
    [aquariumId, queryClient],
  );

  const { items, busy, announcement, addFiles, retry, clearFinished } = usePhotoUploadQueue({
    upload,
    onBatchFinished,
    idPrefix: 'gallery-upload',
    concurrency: GALLERY_UPLOAD_CONCURRENCY,
  });

  return { items, busy, announcement, addFiles, retry, clearFinished };
}
